import { Plus } from "lucide-react";
import { useState } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  useLibraryAssetsQuery,
  type ClosingConversationAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { formatDate } from "@/lib/format";
import { libraryAssetHref } from "@/lib/librarySections";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { Tooltip } from "@/ui/Tooltip";
import {
  EmptyState,
  LoadingState,
  StaleDataNotice,
} from "@/ui/states";

import { AssetStateBadges } from "../AssetStateBadges";
import { CatalogGroup } from "../CatalogGroup";
import { CatalogDetails, CatalogRow } from "../CatalogRow";
import { LibrarySectionHeader } from "../LibrarySectionHeader";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewClosingConversationDialog } from "./NewClosingConversationDialog";

/**
 * The Closing conversation section of the shared clinical library (#867).
 *
 * ## A GROUP OF ITS OWN, WHICH IS THE EXPLICIT ADDITION
 *
 * The S7 screen contract lists this row as "Closing (new)": there is no Lovable
 * closing screen, and what it names instead are the
 * [Pathway library](https://noproductcontent.lovable.app/library/pathways)
 * row/editor patterns to reuse and one addition to make — "Separate Closing
 * conversation group in library". So this section keeps the shared library's
 * compact row, its metadata line and its Details disclosure, in the
 * `CatalogGroup` shell #908 published, and says on screen that the screen itself
 * is new.
 *
 * It is a group of its own rather than a member of Summary or Safety paths
 * because it is neither: the closing agent is the terminal node the pinned
 * engine runs after every final track and before the session ends, and it takes
 * no part in diagnostic selection at all.
 *
 * ## WHAT IT REPLACES
 *
 * The generic `LibraryCatalog`, which listed closing conversations as
 * undifferentiated assets and offered `NewAssetDialog` — a Type picker across
 * families, a parts editor with a free-text "Part key" input, and a raw-JSON
 * textarea for the ordered steps. #866 put the family in that dialog
 * deliberately and named this leaf as its replacement; the raw-key escape hatch
 * is the one the 2026-08-25 canonical amendment already removed for main
 * concern, pathways, BG sets, Summary and Rules.
 */

/** Rows shown before the "Show more" control, bounding the page. */
const PAGE_SIZE = 20;

/**
 * The house sentence for base canon, verbatim.
 *
 * It is the SERVICE's own refusal for a seeded row, and the same string
 * `NamedAssetChrome.protectionExplanation` and every sibling catalog render —
 * one clinical rule with one wording.
 */
const SEEDED_COPY_REASON =
  "Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.";

export function ClosingCatalog({
  sectionLabel,
  sectionDescription,
}: {
  sectionLabel: string;
  /** The section's one sentence, declared beside its rail entry (#908). */
  sectionDescription: string;
}) {
  const [creating, setCreating] = useState(false);
  const [copying, setCopying] = useState<ClosingConversationAsset | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [pages, setPages] = useState(1);
  const assets = useLibraryAssetsQuery(showArchived);
  const write = useCapability("library.write");
  const retryAssets = useRetryPausedQuery(queryKeys.libraryAssets(showArchived));

  // NARROWED, not merely filtered: `LibraryAsset` is discriminated on
  // `assetType`, and the predicate is what lets every row below be typed as this
  // family rather than as the union.
  const items = (assets.data?.items ?? []).filter(
    (asset): asset is ClosingConversationAsset =>
      asset.assetType === "closing_conversation",
  );
  const visible = items.slice(0, pages * PAGE_SIZE);
  const hidden = items.length - visible.length;

  const createControl = (
    <Tooltip
      content={write.reason ?? `Create a new closing conversation`}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={write.reason !== null}
        onClick={() => setCreating(true)}
      >
        <Plus aria-hidden="true" className="h-4 w-4" />
        New closing conversation
      </Button>
    </Tooltip>
  );

  return (
    <>
      <LibrarySectionHeader
        sectionLabel={sectionLabel}
        description={sectionDescription}
        actions={
          <>
            {createControl}
            {/* The house toggle form: a constant label, so `aria-pressed` reads
                as a toggle rather than as a second button, and the variant
                carries the visible state. */}
            <Button
              variant={showArchived ? "primary" : "outline"}
              size="sm"
              aria-pressed={showArchived}
              onClick={() => setShowArchived((value) => !value)}
            >
              Show archived
            </Button>
          </>
        }
      />

      <section
        aria-label={`${sectionLabel} library assets`}
        className="space-y-4"
      >
        {/* SAID ON THE SCREEN. The S7 screen contract requires every UI leaf to
            link its exact reference; this section has none, because Closing is
            an explicit addition rather than a prototype screen. */}
        <p className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          Closing conversation is a new library group, not a copy of an existing
          prototype screen. It reuses the shared library&apos;s compact rows and
          named editors. A closing conversation is never a diagnostic pathway:
          adopting one adds no Always-assessed or Dynamic selection.
        </p>

        {/* A held refresh over cached rows is still a failed refresh: a parked
            retry keeps `status: "success"` with `isError: false`, so testing the
            settled half alone would leave rows of unknown age presenting as
            current. */}
        {assets.data ? (
          <StaleDataNotice
            updatedAt={assets.dataUpdatedAt}
            onRetry={retryAssets}
            visible={
              assets.isPaused ||
              (assets.isError && !isTerminalQueryError(assets.error))
            }
          />
        ) : null}

        {assets.isPaused && assets.data === undefined ? (
          <NamedReadFailureState
            failure={assets.failureReason}
            isPaused
            retry={retryAssets}
            subject="Library assets"
            title="Library assets could not be loaded"
            description="The Editor Service did not return the shared clinical library."
            deniedDescription="You are not authorized to read the shared clinical library."
            offlineDescription="The shared clinical library could not be reached. It loads on its own when the connection returns."
          />
        ) : assets.isPending ? (
          <LoadingState label={`Loading ${sectionLabel} assets`} rows={3} />
        ) : assets.isError &&
          (!assets.data || isTerminalQueryError(assets.error)) ? (
          <QueryErrorState
            error={assets.error}
            subject="Library assets"
            title="Library assets could not be loaded"
            description="The Editor Service did not return the shared clinical library."
            deniedDescription="You are not authorized to read the shared clinical library."
            onRetry={() => void assets.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            title="No closing conversation in the library yet"
            description={
              showArchived
                ? "No closing conversation exists in the library. The base clinical pack ships none — a project that runs one pins a version authored here."
                : "No active closing conversation is in this section. Archived ones stay hidden until shown, and the base clinical pack ships none."
            }
            action={createControl}
          />
        ) : (
          <CatalogGroup
            title="Closing conversations"
            description="One prompt and one ordered step list per closing conversation. A project pins exactly one version, or none at all."
          >
            <Card className="@container overflow-hidden">
              {visible.map((asset) => (
                <ClosingRow
                  key={asset.id}
                  asset={asset}
                  copyRefusal={write.reason}
                  onCopy={() => setCopying(asset)}
                />
              ))}
              {hidden > 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-5 py-3 text-sm text-muted-foreground">
                  <span aria-live="polite">
                    Showing {visible.length} of {items.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPages((value) => value + 1)}
                  >
                    Show {Math.min(hidden, PAGE_SIZE)} more
                  </Button>
                </div>
              ) : null}
            </Card>
          </CatalogGroup>
        )}
      </section>

      {creating ? (
        <NewClosingConversationDialog onClose={() => setCreating(false)} />
      ) : null}
      {copying ? (
        <NewClosingConversationDialog
          copyFrom={copying}
          onClose={() => setCopying(null)}
        />
      ) : null}
    </>
  );
}

function ClosingRow({
  asset,
  copyRefusal,
  onCopy,
}: {
  asset: ClosingConversationAsset;
  /** Why copying is refused, or `null`. Never an unexplained disabled button. */
  copyRefusal: string | null;
  onCopy: () => void;
}) {
  const adoption = asset.capabilities.adoptionCount;

  return (
    <CatalogRow
      to={libraryAssetHref(asset.assetType, asset.id)}
      name={asset.name}
      titleAdornment={<AssetStateBadges capabilities={asset.capabilities} />}
      meta={[
        <span key="version" className="font-mono">
          {asset.version}
        </span>,
        <span key="author" className="min-w-0 truncate">
          {`Created by ${asset.createdBy}`}
        </span>,
        <span key="updated">{`Updated ${formatDate(asset.updatedAt)}`}</span>,
        <span key="adoption">
          {`Used by ${adoption} ${adoption === 1 ? "project" : "projects"}`}
        </span>,
      ]}
      actions={
        // ON EVERY ROW, not only on a seeded one — and that is this family's
        // difference from its siblings rather than an inconsistency with them.
        // `SummaryCatalog`, `MainConcernCatalog` and `PathwayCatalog` all gate
        // this control on `capabilities.seeded`, because for those families the
        // copy exists to answer the service's own refusal of base canon. Base
        // canon ships NO closing conversation — the empty state above says so,
        // and #866's migration records that nothing existing becomes a member of
        // this family — so gating on `seeded` here renders the control only for
        // a row that cannot exist, and the ticket's create/copy requirement
        // would be satisfied by a button nobody can ever reach. Every closing
        // conversation in this catalog is authored, and copying one is the
        // supported way to start a variant of it without touching the versions a
        // project already pins.
        //
        // GATED ON `library.write`, because the dialog it opens ends in
        // `POST /library/assets`. An ungated copy is the dead control ADR 0001
        // forbids: a reader could name the copy and learn only at Create that
        // the service refuses them.
        <Tooltip
          content={
            copyRefusal ??
            (asset.capabilities.seeded
              ? SEEDED_COPY_REASON
              : `Creates your own closing conversation starting from ${asset.name}. The original stays exactly as it is.`)
          }
        >
          <Button
            variant="ghost"
            size="sm"
            className="relative z-10"
            disabled={copyRefusal !== null}
            onClick={onCopy}
          >
            {/* THE ACCESSIBLE NAME CARRIES THE SUBJECT, for `CatalogDetails`'
                reason one control over: a catalog of buttons all named "Create
                editable copy" is the repeated-ambiguous-control defect the
                parity audit already recorded once. Written ONCE in the `sr-only`
                span rather than as a painted word plus a hidden suffix —
                accessible-name computation trims each text node and concatenates
                inline content with no separator, so the two halves would compute
                as one run-together string. The named editor's own copy control
                keeps the bare label: a page carries exactly one. */}
            <span aria-hidden="true">Create editable copy</span>
            <span className="sr-only">
              {`Create an editable copy of ${asset.name}`}
            </span>
          </Button>
        </Tooltip>
      }
      details={
        // The shell's own disclosure, whose accessible name carries the subject
        // — a catalog of rows all named "Details" is the repeated-ambiguous-
        // control defect the parity audit already recorded once.
        <CatalogDetails subject={asset.name}>
          <dl className="grid gap-0.5 font-mono text-[11px] text-muted-foreground">
            <div className="break-all">
              target: conclusion (server-owned; this family has no authored
              placement)
            </div>
            <div className="break-all">
              stage: after every final track, before the session ends
            </div>
          </dl>
        </CatalogDetails>
      }
    />
  );
}
